import {
  computed,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import type { ReportRow } from '../models/report.types'
import {
  resolveRowMedia,
  type ReportMediaAsset,
  type ReportMediaKind,
} from '../service/reportMediaPresentation'

export interface UseReportMedia {
  /** Every creative the row carries; more than one means the row is a breakdown gallery. */
  assets: ComputedRef<ReportMediaAsset[]>
  /** The tile's thumbnail. `null` when the row carries no usable media at all. */
  primary: ComputedRef<ReportMediaAsset | null>
  /** Currently presented carousel asset; starts at the primary asset. */
  active: ComputedRef<ReportMediaAsset | null>
  activeIndex: Ref<number>
  previous: () => void
  next: () => void
  /** True once the browser reports the thumbnail failed to load (blocked, 404, hotlink-denied). */
  broken: Ref<boolean>
  markBroken: () => void
  viewerOpen: Ref<boolean>
  openViewer: () => void
  closeViewer: () => void
}

/**
 * Vue-side of module 08's media seam. All resolution is delegated to the pure
 * `resolveRowMedia` service (ORP-18651 AC4) — this composable owns only the per-tile UI state a
 * pure function cannot: the load-failure flag and whether the lightbox is open.
 */
export function useReportMedia(
  row: MaybeRefOrGetter<ReportRow | null | undefined>,
  kind: MaybeRefOrGetter<ReportMediaKind> = 'image',
): UseReportMedia {
  const assets = computed(() => resolveRowMedia(toValue(row), { kind: toValue(kind) }))
  const primary = computed(() => assets.value[0] ?? null)
  const activeIndex = ref(0)
  const active = computed(() => assets.value[activeIndex.value] ?? primary.value)
  const broken = ref(false)
  const viewerOpen = ref(false)

  // A card grid recycles tiles as pages change. Reset the carousel when the asset set changes, then
  // re-arm the broken-media fallback whenever the selected creative changes.
  watch(
    () => assets.value.map((asset) => asset.src).join('\n'),
    () => {
      activeIndex.value = 0
    },
  )
  watch(
    () => active.value?.src ?? '',
    () => {
      broken.value = false
    },
  )

  return {
    assets,
    primary,
    active,
    activeIndex,
    previous: () => {
      activeIndex.value = Math.max(0, activeIndex.value - 1)
    },
    next: () => {
      activeIndex.value = Math.min(Math.max(0, assets.value.length - 1), activeIndex.value + 1)
    },
    broken,
    markBroken: () => {
      broken.value = true
    },
    viewerOpen,
    openViewer: () => {
      if (active.value) viewerOpen.value = true
    },
    closeViewer: () => {
      viewerOpen.value = false
    },
  }
}
